import { extractApiErrorMessage } from 'lib/helper';
import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchBPAFindingAgeCycles } from '../services/bpaFindingsService';
import { BPAFindingAgeCycle } from '../types/bpaFindings.types';

interface UseBPAFindingAgeHistoryOptions {
  companyId: number;
  enabled: boolean;
  findingId: number;
}

/**
 * Lazily loads and retains a finding's age cycles for the lifetime of the
 * component using this hook. Changing the company or finding clears the
 * retained result so data from one finding cannot be shown for another.
 */
export const useBPAFindingAgeHistory = ({
  companyId,
  enabled,
  findingId,
}: UseBPAFindingAgeHistoryOptions) => {
  const [items, setItems] = useState<BPAFindingAgeCycle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);

  useEffect(() => {
    // Invalidate any request for the previous finding and reset its cached data.
    requestIdRef.current += 1;
    setItems([]);
    setIsLoading(false);
    setHasLoaded(false);
    setError('');
  }, [companyId, findingId]);

  useEffect(
    () => () => {
      // Ignore a response that arrives after the component has been unmounted.
      requestIdRef.current += 1;
    },
    [],
  );

  const load = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);
    setError('');

    if (!companyId || companyId === -1) {
      setError('Please select a company before viewing age history.');
      setHasLoaded(true);
      setIsLoading(false);
      return;
    }

    try {
      const result = await fetchBPAFindingAgeCycles(findingId, companyId);
      if (requestIdRef.current !== requestId) return;

      if (result === null) {
        setError('Age history request was cancelled. Please retry.');
      } else {
        setItems(result);
      }
    } catch (err: unknown) {
      if (requestIdRef.current === requestId) {
        setError(
          extractApiErrorMessage(err, 'Failed to fetch finding age history.'),
        );
      }
    } finally {
      if (requestIdRef.current === requestId) {
        // A completed request, including an error, must not automatically loop.
        // The user can explicitly request another attempt through Retry.
        setHasLoaded(true);
        setIsLoading(false);
      }
    }
  }, [companyId, findingId]);

  useEffect(() => {
    if (enabled && !hasLoaded && !isLoading) {
      void load();
    }
  }, [enabled, hasLoaded, isLoading, load]);

  return {
    error,
    isLoading: isLoading || (enabled && !hasLoaded && !error),
    items,
    retry: load,
  };
};
